import type { DocumentTemplate } from "@models";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import {
  DataGridPremium,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentScopeChip } from "src/components/Documents/DocumentScopeChip";
import { DocumentsPageViewToggle } from "src/components/Documents/DocumentsPageViewToggle";
import { formatDocumentTableDate } from "src/components/Documents/DocumentsTableDates";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { CompactSelect } from "src/components/ui/CompactSelect";
import { ROUTES, ROUTE_SLUGS } from "src/config/navigation/routes";
import { useGlobalDeviceContext } from "src/hooks";
import { useGetDevices, useGetDocumentsTemplates } from "src/hooks/queries";
import { fillRouteWithSlugs } from "src/utils";
import { filterFrameTemplates } from "./Frames.utils";
import { FramesBreadcrumb } from "./FramesBreadcrumb";

const COLUMNS: GridColDef<DocumentTemplate>[] = [
  {
    field: "defaultTitle",
    headerName: "Title",
    flex: 1,
    sortable: false,
  },
  {
    field: "functionalGroupAcronym",
    headerName: "Group",
    width: 110,
    sortable: false,
    valueGetter: (value) => value ?? "",
  },
  {
    field: "regulatoryType",
    headerName: "Type",
    width: 110,
    sortable: false,
    valueGetter: (value) => value ?? "",
  },
  {
    field: "isOrgDocument",
    headerName: "Scope",
    width: 130,
    sortable: false,
    renderCell: (params: GridRenderCellParams<DocumentTemplate>) => {
      const owner = params.row.isOrgDocument ? "Organization" : "Device";

      return (
        <DocumentScopeChip
          dataTestId="frame-scope-chip"
          label={owner}
          tooltipTitle={`Template Belongs To: ${owner}`}
        />
      );
    },
  },
  {
    field: "updatedAt",
    headerName: "Updated",
    width: 150,
    sortable: false,
    valueFormatter: (value?: string) => formatDocumentTableDate(value),
  },
];

type FramesViewProps = {
  orgId: string;
};

/**
 * The `?view=frames` mode of the documents page: a Documents/Frames breadcrumb
 * header plus a searchable template list.
 */
export const FramesView = ({ orgId }: FramesViewProps) => {
  const navigate = useNavigate();
  const { activeGlobalDeviceId, setActiveGlobalDeviceId } =
    useGlobalDeviceContext();
  const [search, setSearch] = useState("");
  const devicesQuery = useGetDevices({ orgId });
  const devices = devicesQuery.data ?? [];
  const selectedDevice = devices.find(
    (device) => device.id === activeGlobalDeviceId
  );
  const templatesQuery = useGetDocumentsTemplates({
    orgId,
    deviceId: activeGlobalDeviceId ?? "",
    enabled: !!activeGlobalDeviceId,
  });

  useEffect(() => {
    if (activeGlobalDeviceId || !devices.length) {
      return;
    }

    setActiveGlobalDeviceId(devices[0].id);
  }, [activeGlobalDeviceId, devices, setActiveGlobalDeviceId]);

  // Loading covers the device auto-select window too, so the grid never
  // flashes empty before the first device kicks off the templates fetch.
  const isDeviceResolving =
    devicesQuery.isLoading || (!activeGlobalDeviceId && devices.length > 0);
  const isLoading =
    isDeviceResolving || (!!activeGlobalDeviceId && templatesQuery.isLoading);
  const rows = useMemo(
    () =>
      filterFrameTemplates(Object.values(templatesQuery.data ?? {}), search),
    [search, templatesQuery.data]
  );

  const openFrame = (template: DocumentTemplate) => {
    const route = fillRouteWithSlugs(ROUTES.FRAME_VIEWER, {
      [ROUTE_SLUGS.ORG_ID]: orgId,
      [ROUTE_SLUGS.TEMPLATE_ID]: template.id,
    });

    navigate(`${route}?deviceId=${activeGlobalDeviceId}`);
  };

  return (
    <>
      <PageHeader
        pageTitleComponent={
          <FramesBreadcrumb
            items={[
              {
                label: "Documents",
                to: fillRouteWithSlugs(ROUTES.DOCUMENTS, {
                  [ROUTE_SLUGS.ORG_ID]: orgId,
                }),
              },
              { label: "Frames" },
            ]}
          />
        }
        rightButtons={<DocumentsPageViewToggle />}
        rightPanelToggleRequiresDevice={false}
      />

      <Box
        data-testid="frames-view"
        sx={{
          backgroundColor: "#FFFFFF",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          gap={1}
          data-testid="frames-toolbar"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            boxSizing: "border-box",
            flex: "0 0 42px",
            height: "42px",
            paddingLeft: "10px",
            paddingRight: "7px",
          }}
        >
          {devices.length > 0 && (
            <CompactSelect
              value={activeGlobalDeviceId ?? ""}
              onChange={setActiveGlobalDeviceId}
              ariaLabel="Device scope"
              renderValue={() => selectedDevice?.name ?? "Select device"}
            >
              <ListSubheader>Templates for</ListSubheader>
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name}
                </MenuItem>
              ))}
            </CompactSelect>
          )}
          <TextField
            fullWidth
            hiddenLabel
            inputProps={{ "aria-label": "Search frames" }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
            size="small"
            value={search}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    aria-hidden="true"
                    sx={{ color: "rgba(100, 116, 139, 0.9)", fontSize: 16 }}
                  />
                </InputAdornment>
              ),
              sx: {
                fontSize: 14,
                "& input": {
                  height: 28,
                  lineHeight: "28px",
                  py: 0,
                },
              },
            }}
          />
        </Stack>
        <DataGridPremium
          aria-label="Frames"
          rows={rows}
          columns={COLUMNS}
          getRowId={(row) => row.id}
          onRowClick={(params) => {
            if (!isLoading) {
              openFrame(params.row);
            }
          }}
          loading={isLoading}
          columnHeaderHeight={42}
          sx={{
            border: "none",
            boxShadow: "none",
            flex: 1,
            fontSize: "14px",
            minHeight: 0,
            outline: "none",
            "&.MuiDataGrid-root": {
              border: "none",
              boxShadow: "none",
              outline: "none",
            },
            ".MuiDataGrid-withBorderColor": {
              borderColor: "transparent",
            },
            ".MuiDataGrid-columnHeaders, .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
            ".MuiDataGrid-columnHeader": {
              backgroundColor: "#FFFFFF",
              borderBottom: "none",
              outline: "none",
              padding: "9px 10px",
            },
            ".MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-row:focus, .MuiDataGrid-row:focus-within":
              {
                outline: "none",
              },
            ".MuiDataGrid-row": {
              borderBottom: "none",
              cursor: "pointer",
              outline: "none",
            },
            ".MuiDataGrid-cell": {
              alignItems: "center",
              borderBottom: "none",
              display: "flex",
              outline: "none",
              px: "10px",
            },
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnReorder
          hideFooter
          localeText={{ noRowsLabel: "No frames found" }}
          slotProps={{
            loadingOverlay: {
              noRowsVariant: "skeleton",
              variant: "skeleton",
            },
          }}
        />
      </Box>
    </>
  );
};
